/**
 * Phase 130B — StreamX Waitlist Super Admin Dashboard
 *
 * Renders aggregated demand signals + a paginated lead table for super
 * admins.  Pure read-only surface; CSV export is computed client-side.
 */

import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Download, RefreshCw, Radio, Users, MapPin, Trophy, Sparkles, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeader() {
  const t = localStorage.getItem('outplay_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function toCsv(rows) {
  if (!rows || !rows.length) return '';
  const cols = ['created_at', 'email', 'academy_name', 'contact_person', 'mobile', 'city',
                'sports_type', 'expected_streams_per_month', 'current_streaming_method',
                'interested_features', 'source'];
  const esc = (v) => {
    if (v === null || v === undefined) return '';
    const s = Array.isArray(v) ? v.join('|') : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const head = cols.join(',');
  const body = rows.map((r) => cols.map((c) => esc(r[c])).join(',')).join('\n');
  return `${head}\n${body}`;
}

export default function LivexWaitlistDashboard() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/admin/livex/waitlist?limit=500`, { headers: authHeader() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleExport = () => {
    if (!data?.entries?.length) return;
    const csv = toCsv(data.entries);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `livex-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const stats = data?.stats;

  return (
    <div className="min-h-screen bg-background" data-testid="livex-waitlist-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Link to="/super-admin" data-testid="livex-dashboard-back">
              <Button variant="ghost" size="sm" className="gap-1.5 h-9">
                <ArrowLeft className="h-4 w-4" /> Super Admin
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <Radio className="h-5 w-5 text-red-600" /> StreamX Waitlist
              </h1>
              <p className="text-xs text-muted-foreground mt-0.5">
                Demand-validation signal — pre-build pilot pipeline for the StreamX broadcasting product.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="livex-dashboard-refresh" className="h-9 gap-1.5">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Refresh
            </Button>
            <Button size="sm" onClick={handleExport} disabled={!data?.entries?.length} data-testid="livex-dashboard-export" className="h-9 gap-1.5">
              <Download className="h-4 w-4" /> Export CSV
            </Button>
          </div>
        </div>

        {error && (
          <Card className="border-red-200 bg-red-50/40">
            <CardContent className="p-4 text-sm text-red-800">{error}</CardContent>
          </Card>
        )}

        {/* Top-line KPI strip */}
        <div className="grid sm:grid-cols-4 gap-3" data-testid="livex-dashboard-kpis">
          <KpiCard icon={Users}    label="Total signups"       value={data?.total ?? '—'} />
          <KpiCard icon={MapPin}   label="Top city"            value={stats?.top_cities?.[0]?.key || '—'} hint={stats?.top_cities?.[0]?.count ? `${stats.top_cities[0].count} signups` : null} />
          <KpiCard icon={Trophy}   label="Top sport"           value={stats?.top_sports?.[0]?.key || '—'} hint={stats?.top_sports?.[0]?.count ? `${stats.top_sports[0].count} signups` : null} />
          <KpiCard icon={Sparkles} label="Full profiles"       value={stats?.funnel?.with_full_profile ?? '—'} hint="Email + academy + mobile + city" />
        </div>

        {/* Aggregation panels */}
        <div className="grid lg:grid-cols-3 gap-4">
          <BreakdownCard title="Cities" items={stats?.top_cities} testid="livex-stat-cities" />
          <BreakdownCard title="Sports" items={stats?.top_sports} testid="livex-stat-sports" />
          <BreakdownCard title="Feature interest" items={stats?.top_features} testid="livex-stat-features" />
          <BreakdownCard title="Currently streaming via" items={stats?.top_current_methods} testid="livex-stat-methods" />
          <BreakdownCard title="Expected streams / month" items={stats?.top_streams_per_month} testid="livex-stat-streams" />
          <Card data-testid="livex-stat-funnel">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-red-600"/> Lead-quality funnel</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {[
                ['Email only',         stats?.funnel?.email_only],
                ['With academy name',  stats?.funnel?.with_academy],
                ['With mobile',        stats?.funnel?.with_mobile],
                ['Full profile',       stats?.funnel?.with_full_profile],
              ].map(([label, v]) => (
                <div key={label} className="flex justify-between text-xs">
                  <span className="text-muted-foreground">{label}</span>
                  <span className="font-mono font-bold">{v ?? '—'}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Lead table */}
        <Card data-testid="livex-dashboard-table">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Pipeline ({data?.shown ?? 0} of {data?.total ?? 0})</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-2.5 font-semibold">Submitted</th>
                  <th className="px-4 py-2.5 font-semibold">Academy</th>
                  <th className="px-4 py-2.5 font-semibold">Contact</th>
                  <th className="px-4 py-2.5 font-semibold">City · Sport</th>
                  <th className="px-4 py-2.5 font-semibold">Volume · Today</th>
                  <th className="px-4 py-2.5 font-semibold">Interests</th>
                </tr>
              </thead>
              <tbody>
                {(data?.entries || []).map((e) => (
                  <tr key={e.id} className="border-t hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-xs">{e.academy_name || <span className="text-muted-foreground italic">—</span>}</div>
                      <div className="text-[11px] text-muted-foreground font-mono">{e.email}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {e.contact_person || <span className="text-muted-foreground italic">—</span>}
                      {e.mobile && <div className="text-[11px] text-muted-foreground font-mono">{e.mobile}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div>{e.city || <span className="text-muted-foreground italic">—</span>}</div>
                      <div className="text-[11px] text-muted-foreground capitalize">{e.sports_type || '—'}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div>{e.expected_streams_per_month ? `${e.expected_streams_per_month} / mo` : <span className="text-muted-foreground italic">—</span>}</div>
                      <div className="text-[11px] text-muted-foreground">{e.current_streaming_method ? e.current_streaming_method.replace(/_/g, ' ') : '—'}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(e.interested_features || []).map((f) => (
                          <span key={f} className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200">
                            {f.replace(/_/g, ' ')}
                          </span>
                        ))}
                        {!(e.interested_features?.length) && <span className="text-[11px] text-muted-foreground italic">—</span>}
                      </div>
                    </td>
                  </tr>
                ))}
                {!loading && !(data?.entries?.length) && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-muted-foreground">No waitlist entries yet.</td></tr>
                )}
                {loading && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
                  </td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, hint }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
          <Icon className="h-3.5 w-3.5" /> {label}
        </div>
        <div className="mt-1 text-2xl font-extrabold tracking-tight">{value}</div>
        {hint && <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>}
      </CardContent>
    </Card>
  );
}

function BreakdownCard({ title, items, testid }) {
  const max = useMemo(() => Math.max(1, ...(items || []).map((i) => i.count)), [items]);
  return (
    <Card data-testid={testid}>
      <CardHeader className="pb-2"><CardTitle className="text-sm">{title}</CardTitle></CardHeader>
      <CardContent className="space-y-1.5">
        {(items?.length ? items : []).map((it) => (
          <div key={it.key} className="flex items-center gap-2">
            <span className="text-xs flex-1 truncate capitalize">{(it.key || '').replace(/_/g, ' ')}</span>
            <div className="w-24 h-1.5 rounded-full bg-muted overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${(it.count / max) * 100}%`, background: 'linear-gradient(90deg, #dc2626, #f97316)' }} />
            </div>
            <span className="text-[11px] font-mono font-bold w-6 text-right">{it.count}</span>
          </div>
        ))}
        {!items?.length && <div className="text-[11px] text-muted-foreground italic">No data yet</div>}
      </CardContent>
    </Card>
  );
}
